import { TitleCasePipe }                                      from '@angular/common';
import { isNullOrUndefined, isString }                        from '@cs/core/utils';
import * as Sentry                                            from '@sentry/browser';
import { CsLoggerOptions }                                    from '../classes/cs-logger-options';
import { Environment }                                        from '../classes/cs-enviroment';
import { ILoggerBreadcrumb, ILoggerProvider, LoggerResponse } from '../classes/logger-provider.interface';
import { Result }                                             from '@cs/core';
import { LoggerUtil }                                         from '@cs/core';


export class CsRavenOptions extends CsLoggerOptions {
	dns: string;
	options: {
		[key: string]: string
	};
	/**
	 * Performance tracing sample rate (0..1). Leave undefined to disable tracing.
	 * When set, provide a tracing integration via {@link integrations}.
	 */
	tracesSampleRate?: number;
	/**
	 * Extra Sentry integrations to register, e.g. browserTracingIntegration().
	 * Passed in by the host app so this generic provider stays framework-agnostic.
	 * Typed off Sentry.init so it always matches the installed SDK version.
	 */
	integrations?: NonNullable<Parameters<typeof Sentry.init>[0]>['integrations'];
	/**
	 * Same-origin path to tunnel Sentry envelopes through (Sentry `tunnel` option).
	 * Set this when the Sentry host is only reachable on a private network, so the
	 * browser POSTs to our own origin (a server-side proxy forwards to Sentry) and
	 * avoids Chrome's Local Network Access prompt / ad-blockers. e.g. '/_api/sentry'.
	 */
	tunnel?: string;

	constructor(init: {
		dns: string,
		options: {
			[key: string]: string
		},
		version: string,
		environment: Environment,
		enableErrorLogging?: boolean,
		rateLimitCount?: number,
		rateLimitPeriod?: number
	}) {
		super(init.version, init.environment);

		const {dns, options} = init;
		this.dns             = dns;
		this.options         = options;
	}

}

export class SentryLogger implements ILoggerProvider<CsRavenOptions> {
	options: CsRavenOptions;

	constructor(options: CsRavenOptions) {
		this.options = options;
	}

	captureException(e: Error): Promise<Result<LoggerResponse>> {
		return new Promise<Result<LoggerResponse>>(resolve => {
			const result = Sentry.captureException(e);
			LoggerUtil.error(result);
			const response       = new LoggerResponse();
			response.referenceId = result;
			resolve(Result.success(response));
		});
	}

	setupErrorProvider(): Promise<boolean> {

		const serverErrorsRegex = new RegExp(
			'500 Internal Server Error|401 Unauthorized|Http failure.+401|Http failure.+403|403 Forbidden|404 Not Found|502 Bad Gateway|503 Service Unavailable|rest\\/app\\/keep-alive',
			'mi'
		);

		Sentry.init({
															dsn: this.options.dns,
															// set application version
															release:     this.options.version,
															environment: new TitleCasePipe().transform(this.options.environment),
															// Route envelopes through a same-origin proxy when set (avoids the
															// Local Network Access prompt for a privately-hosted Sentry).
															tunnel:           this.options.tunnel,
															// Drop errors thrown by browser extensions / Firefox reader mode
															// (their scripts run in the page but are not our code).
															// `window.__firefox__` is injected by the Firefox reader extension (IJS-359).
															ignoreErrors:     [/__firefox__/],
															denyUrls:         [/^moz-extension:\/\//i, /^about:reader/i, /^resource:\/\//i],
															// Optional performance tracing (host app opts in by supplying a
															// tracing integration + sample rate). Undefined = tracing disabled.
															integrations:     this.options.integrations ?? [],
															tracesSampleRate: this.options.tracesSampleRate,
															beforeSend(event, hint) {
																// We ignore Server Errors. We have to define here since Angular
																// http client uses setTimeout to detect http call progress.
																// And when the call fails, it throws an exception inside that timeout
																// that bubbles up higher to the main Angular's error handler.

					const errorMessage = isString(hint.originalException)
																																					? hint.originalException
																																					: (hint.originalException as any)?.message || event.message || '';

																if (new RegExp('Unknown Error').test(errorMessage)) {
																	event.tags = {
																		...event.tags, // Preserve existing tags
																		httpErrorCode: '0'
																	};
																}

																if (serverErrorsRegex.test(errorMessage)) {
																	// Drop the event
																	return null;
																}

																// Send the event
																return event;
															}
														});

		return Promise.resolve(true);
	}

	addBreadcrumb(param: ILoggerBreadcrumb): void {

		Sentry.addBreadcrumb({
																								data:      param.data,
																								category:  param.category,
																								type:      param.type,
																								timestamp: Date.now(),
																								level:     'log',
																								message:   param.message
																							});

	}

}
